import React, { useState, useEffect, useRef } from 'react';
import apiService from '../../utils/api';
import './CreateBlankModal.css';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import ProjectPopup from './ProjectPopup';
import ClientPopup from './ClientPopup';
import EmailTemplate from '../../utils/EmailTemplate';
import VendorPopup from './VendorPopup';
import { validateForm, getSelectedContractTypeLabel, isEndDateInPast } from './validationHelpers';
import { createInputChangeHandler } from './inputChangeHelpers';
import { buildUploadPayload, buildTemplatePayload, buildBlankPayload } from './payloadBuilders';


export function CustomSelect({ options, value, onChange, placeholder = "Select...", disabled, className, renderAsSpan, multiSelect = false }) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [searchTerm, setSearchTerm] = useState("");
  const btnRef = useRef(null);
  const listRef = useRef(null);
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  const selected = multiSelect
    ? options.filter(o => (value || []).includes(o.value))
    : options.find(o => o.value === value);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const onDocClick = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const openAndHighlight = () => {
    setOpen(true);
    setSearchTerm("");
    const idx = Math.max(0, filteredOptions.findIndex(o => o.value === value));
    setHighlight(idx === -1 ? 0 : idx);
    setTimeout(() => searchRef.current?.focus(), 0);
  };

  const handleButtonKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openAndHighlight();
    }
  };

  const handleListKeyDown = (e) => {
    if (e.key === "Escape") setOpen(false);
    if (e.key === "ArrowDown") setHighlight(h => Math.min(filteredOptions.length - 1, h + 1));
    if (e.key === "ArrowUp") setHighlight(h => Math.max(0, h - 1));
    if (e.key === "Enter" && highlight >= 0 && filteredOptions[highlight]) {
      if (multiSelect) {
        select(filteredOptions[highlight].value);
      } else {
        onChange(filteredOptions[highlight].value);
        setOpen(false);
        btnRef.current?.focus();
      }
    }
  };

  const select = (val) => {
    if (multiSelect) {
      const currentValues = value || [];
      const newValues = currentValues.includes(val)
        ? currentValues.filter(v => v !== val)
        : [...currentValues, val];
      onChange(newValues);
    } else {
      onChange(val);
      setOpen(false);
      setSearchTerm("");
      btnRef.current?.focus();
    }
  };

  const displayOptions = filteredOptions.length === 0 ? [{ label: "No options found", value: "" }] : filteredOptions;

  const displayText = multiSelect
    ? (selected.length > 0 ? selected.map(s => s.label).join(', ') : placeholder)
    : (selected ? selected.label : placeholder);

  return (
    <div className={`cr-select ${className || ''} ${open ? "open" : ""} ${disabled ? "disabled" : ""}`} ref={rootRef}>
      <span
        ref={btnRef}
        className="cr-select__button"
        onClick={() => !disabled && setOpen(o => !o)}
        onKeyDown={handleButtonKeyDown}
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`cr-select__value ${!selected || (multiSelect && selected.length === 0) ? "placeholder" : ""}`}>
          {displayText}
        </span>
        {/* <span className="dropdown-icon">▼</span>  */}
        <span className="dropdown-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>

      </span>
      {open && (
        <div className="cr-select__dropdown">
          <input
            ref={searchRef}
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setHighlight(0);
            }}
            onKeyDown={handleListKeyDown}
            style={{
              width: "97%",
              padding: "8px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              marginBottom: "4px",
              outline: "none",
              marginLeft: "10px"
            }}
          />
          <ul
            className="cr-select__list"
            role="listbox"
            tabIndex={-1}
            ref={listRef}
            onKeyDown={handleListKeyDown}
          >
            {displayOptions.map((opt, i) => {
              const isSelected = multiSelect
                ? (value || []).includes(opt.value)
                : opt.value === value;
              const isHighlighted = i === highlight;
              return (
                <li
                  key={opt.value ?? i}
                  role="option"
                  aria-selected={isSelected}
                  className={`cr-select__option${isSelected ? " selected" : ""}${isHighlighted ? " highlighted" : ""}`}
                  onMouseEnter={() => setHighlight(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => select(opt.value)}
                  title={opt.label}
                >
                  {multiSelect && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      readOnly
                      style={{ marginRight: '8px', pointerEvents: 'none' }}
                    />
                  )}
                  {opt.label}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

const CreateBlankModal = ({ isOpen, onClose, onSave, uploadedFile, parentNode, parentContractId, selectedTemplate, prefillContractData, isPrefillLoading }) => {
  const [isMainMaximized, setIsMainMaximized] = useState(false);
  const [isPreviewMaximized, setIsPreviewMaximized] = useState(false);
  const [formData, setFormData] = useState({
    contractTitle: '',
    contractType: '',
    parent_contract_id: '',
    description: '',
    ownerDepartment: '',
    clientName: '',
    clientEmail: '',
    vendorName: '',
    vendorEmail: '',
    startDate: '',
    endDate: '',
    renewalDate: '',
    status_id: '',
    region_id: '',
    project_id: '',
    year: '',
    po_number: '',
    net_term: '',
    bill_rate_currency: '',
    billing_country: '',
    termination_date: '',
    termination_reason: '',
    practices: '',
    billing_type: '',
    metatag: '',
    contract_value: '',
    currency_type: 'INR',
    subsidiary: '',
    billing_frequency: '',
    execution_type: [],
    hsn_code: '',
    tax_code: ''
  });
  const [itemDetails, setItemDetails] = useState([{ item: '', quantity: '' }]);
  const [sowDetails, setSowDetails] = useState([{ role_name: '', role_rate: '', sow_amount: '', sow_hours: '' }]);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [contractTypes, setContractTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [regions, setRegions] = useState([]);
  const [apiError, setApiError] = useState(null);
  const [clients, setClients] = useState([]);
  const [emails, setEmails] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorEmails, setVendorEmails] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [userId, setUserId] = useState('');
  const [isProjectPopupOpen, setIsProjectPopupOpen] = useState(false);
  const [isClientPopupOpen, setIsClientPopupOpen] = useState(false);
  const [isVendorPopupOpen, setIsVendorPopupOpen] = useState(false);
  const [parentContracts, setParentContracts] = useState([]);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isAmendment, setIsAmendment] = useState(false);
  const [isAddendum, setIsAddendum] = useState(false);
  const [projectPopupPrefix, setProjectPopupPrefix] = useState('');
  const [isAutoRenewal, setIsAutoRenewal] = useState(false);
  const projectRef = useRef(null);
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    node: null,
    contractId: null,
  });
  const [contractSelection, setContractSelection] = useState('new');
  const [netTerms] = useState([
    { label: "30 Days", value: "30" },
    { label: "45 Days", value: "45" },
    { label: "60 Days", value: "60" },
    { label: "90 Days", value: "90" },
    { label: "Pay-on-Pay", value: "pay-on-pay" }
  ]);
  const [currencies, setCurrencies] = useState([]);
  const [practices, setPractices] = useState([]);
  const [billingTypes, setBillingTypes] = useState([]);
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [billingFrequencies] = useState([
    { label: "Weekly", value: "weekly" },
    { label: "Monthly", value: "monthly" },
    { label: "Fortnite", value: "fortnite" }
  ]);
  const [executionTypes] = useState([
    { label: "Off Site", value: "off-site" },
    { label: "On-Site", value: "on-site" },
    { label: "Nearshore", value: "near-shore" }
  ]);

  const selectedStatusObj = Array.isArray(statuses)
    ? statuses.find(s => String(s.id) === String(formData.status_id) || String(s.value) === String(formData.status_id))
    : null;
  const isCompletedContract = selectedStatusObj?.value?.trim().toLowerCase() === 'completed';
  const isEndDatePast = isEndDateInPast(formData.endDate, isAutoRenewal);
  const isRenewalDateOptional = isCompletedContract || isEndDatePast;

  // Generate years from 2014 to current year
  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = 2014; year <= currentYear; year++) {
      years.push({ label: year.toString(), value: year.toString() });
    }
    return years.reverse(); // Most recent year first
  };

  // 🔹 Normalizes API/fallback data to {id, value}
  const normalizeData = (data, fallback) => {
    if (Array.isArray(data)) {
      return data.map((item, index) =>
        typeof item === 'object'
          ? { id: item.id || index, value: item.value }
          : { id: index, value: item }
      );
    }
    return fallback.map((val, index) => ({ id: index, value: val }));
  };

  const fetchClients = async () => {
    try {
      const clientData = await apiService.fetchData('add_client');
      setClients(clientData.clients.results || []);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const fetchVendors = async () => {
    try {
      const vendorData = await apiService.fetchData('vendor');
      setVendors(vendorData.data || []);
    } catch (error) {
      console.error("Error fetching vendors:", error);
      setVendors([]);
    }
  };
  const fetchProjects = async () => {
    try {
      const projectData = await apiService.fetchData('ruddr/project');
      setProjects(projectData.data || []);
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchClients();
      fetchVendors();
      fetchProjects();
      fetchParentContracts();
    }
  }, [isOpen]);


  useEffect(() => {
    if (!isOpen) return;
    const fetchDropdownData = async () => {
      try {
        const contractTypeData = await apiService.fetchData('metadata/contract-type');
        setContractTypes(normalizeData(contractTypeData, ['NDA', 'MSA', 'SOW']));

        const departmentData = await apiService.fetchData('metadata/department');
        setDepartments(normalizeData(departmentData, ['Legal', 'Finance', 'TAG']));

        const statusData = await apiService.fetchData('metadata/contract-status');
        setStatuses(normalizeData(statusData, ['Draft', 'Pending Approval', 'Approved']));

        const regionData = await apiService.fetchData('metadata/region');
        setRegions(normalizeData(regionData, ['North', 'South', 'East', 'West']));

        const clientData = await apiService.fetchData('add_client');
        setClients(clientData.clients.results || []);

        const practicesData = await apiService.fetchData('practices');
        setPractices(
          practicesData.data?.map(p => ({
            label: p.name,
            value: p.name
          })) || []
        );
        console.log("Practices API Response:", practicesData);


        const billingTypeData = await apiService.fetchData('billing-types');
        setBillingTypes(
          billingTypeData.data?.map(bt => ({
            label: bt.name,
            value: bt.name
          })) || []
        );
        console.log("Billing Types API Response:", billingTypeData);


        const currenciesData = await apiService.fetchData('currencies');
        setCurrencies(
          currenciesData.data?.map(c => ({
            label: c.name,
            value: c.code
          })) || []
        );
        console.log("Currencies API Response:", currenciesData);

        const subsidiariesData = await apiService.fetchData('cr-entities');
        setSubsidiaries(
          subsidiariesData.data?.map(e => ({
            label: e.entity_name,
            value: e.entity_name
          })) || []
        );
        console.log("Subsidiaries API Response:", subsidiariesData);

        const projectData = await apiService.fetchData('ruddr/project');
        setProjects(projectData.data || []);

      } catch (error) {
        console.error("Dropdown API error:", error);
        setApiError('Failed to load some dropdown options. Using defaults.');

        // fallback values
        setContractTypes(normalizeData([], ['NDA', 'MSA', 'SOW']));
        setDepartments(normalizeData([], ['Legal', 'Finance', 'TAG']));
        setStatuses(normalizeData([], ['Draft', 'Pending Approval', 'Approved']));
        setRegions(normalizeData([], ['North', 'South', 'East', 'West']));
        setPractices([]);
        setBillingTypes([]);
        setCurrencies([]);
        setSubsidiaries([]);
      }
    };

    fetchDropdownData();
  }, [isOpen]);

  useEffect(() => {
    const storedUserId = sessionStorage.getItem('user_id') || '';
    const storedName = sessionStorage.getItem('user_name') || '';
    const storedEmail = sessionStorage.getItem('user_emailId') || '';
    setUserId(storedUserId);
    setFormData(prev => ({ ...prev, user_id: storedUserId, user_name: storedName, email: storedEmail }));
  }, []);

  // Pre-fill form when prefillContractData is provided (e.g., clicking + on a contract type folder)
  useEffect(() => {
    if (!isOpen || !prefillContractData) return;

    console.log("Pre-filling form with parent contract data:", prefillContractData);

    const data = prefillContractData;

    // Determine business type based on client_id and vendor_id
    let businessType = '';
    if (data.client_id && Number(data.client_id) > 0) {
      businessType = 'Client';
    } else if (data.vendor_id && Number(data.vendor_id) > 0) {
      businessType = 'Vendor';
    }

    // Find contract type ID from the fetched contract types
    let contractTypeId = '';
    if (data.TypeID) {
      contractTypeId = String(data.TypeID);
    }

    // Find department ID
    let ownerDept = '';
    if (data.owner_department_id && data.owner_department_id !== 'N/A') {
      ownerDept = data.owner_department_id;
    }

    // Find status ID from the status name
    let statusId = '';
    if (data.contract_status && statuses.length > 0) {
      const matchedStatus = statuses.find(s => s.value === data.contract_status);
      if (matchedStatus) statusId = String(matchedStatus.id);
    }

    // Find region ID from the region name
    let regionId = '';
    if (data.RegionName && regions.length > 0) {
      const matchedRegion = regions.find(r => r.value === data.RegionName);
      if (matchedRegion) regionId = String(matchedRegion.id);
    }

    // Find project ID from the project name
    let projectId = '';
    if (data.project_name && data.project_name !== 'No Project' && projects.length > 0) {
      const matchedProject = projects.find(p => p.name === data.project_name);
      if (matchedProject) projectId = String(matchedProject.id);
    }

    // Handle client email population
    if (businessType === 'Client' && data.client_id) {
      const selectedClient = clients.find(c => c.client_id === Number(data.client_id));
      if (selectedClient) {
        const clientEmails = [
          selectedClient.clientEmail,
          selectedClient.salesRepresentativeEmail
        ].filter(Boolean);
        setEmails(clientEmails);
      }
    }

    // Handle vendor email population
    if (businessType === 'Vendor' && data.vendor_id) {
      const selectedVendor = vendors.find(v => v.vendor_id === Number(data.vendor_id));
      if (selectedVendor) {
        const vEmails = [selectedVendor.vendorEmail].filter(Boolean);
        setVendorEmails(vEmails);
      }
    }

    // Format dates - handle auto-renewal sentinel values
    const formatDate = (dateVal) => {
      if (!dateVal || dateVal === 'N/A') return '';
      const s = String(dateVal);
      if (s.startsWith('1999-01-01') || s.startsWith('1900-01-01')) return '1999-01-01';
      return s.slice(0, 10);
    };

    // Check if auto-renewal
    const endDateStr = data.end_date ? String(data.end_date) : '';
    const isAutoRenewalVal = endDateStr.startsWith('1999-01-01');
    setIsAutoRenewal(isAutoRenewalVal);

    // Set parent contract ID to the current contract's ID
    const parentId = data.contract_id ? String(data.contract_id).replace('CONTRACT-', '') : '';

    setFormData(prev => ({
      ...prev,
      contractTitle: '',  // Leave blank for new contract title
      contractType: contractTypeId,
      parent_contract_id: parentId,
      description: '',  // Leave blank for new description
      ownerDepartment: ownerDept,
      businessType: businessType,
      clientName: businessType === 'Client' ? Number(data.client_id) : '',
      clientEmail: businessType === 'Client' ? (data.party_email || '') : '',
      vendorName: businessType === 'Vendor' ? Number(data.vendor_id) : '',
      vendorEmail: businessType === 'Vendor' ? (data.party_email || '') : '',
      startDate: formatDate(data.start_date),
      endDate: isAutoRenewalVal ? '1999-01-01' : formatDate(data.end_date),
      renewalDate: formatDate(data.renewal_date),
      status_id: statusId,
      region_id: regionId,
      project_id: projectId,
      po_number: data.po_no || '',
      net_term: data.net_term || '',
      bill_rate_currency: data.bill_rate_currency || '',
      billing_country: data.billing_country || '',
      practices: data.practices || '',
      billing_type: data.billing_type || '',
      metatag: data.metatag || '',
      subsidiary: data.cr_entity || '',
      billing_frequency: data.billing_frequency || '',
      execution_type: data.execution_type ? (typeof data.execution_type === 'string' ? data.execution_type.split(',').map(s => s.trim()) : data.execution_type) : [],
      hsn_code: data.hsn_code || '',
      tax_code: data.tax_code || '',
    }));

    // Pre-fill item details if available
    if (data.item_details) {
      try {
        const items = typeof data.item_details === 'string' ? JSON.parse(data.item_details) : data.item_details;
        if (Array.isArray(items) && items.length > 0) {
          setItemDetails(items.map(i => ({ item: i.item || i.item_name || '', quantity: i.quantity || '' })));
        }
      } catch (e) {
        console.error('Error parsing prefill item details:', e);
      }
    }

    // Pre-fill SOW details if available
    if (data.sow_details) {
      try {
        const sows = typeof data.sow_details === 'string' ? JSON.parse(data.sow_details) : data.sow_details;
        if (Array.isArray(sows) && sows.length > 0) {
          setSowDetails(sows.map(s => ({
            role_name: s.role_name || '',
            role_rate: s.role_rate || '',
            sow_amount: s.sow_amount || '',
            sow_hours: s.sow_hours || '',
          })));
        }
      } catch (e) {
        console.error('Error parsing prefill SOW details:', e);
      }
    }
  }, [isOpen, prefillContractData, contractTypes, statuses, regions, projects, clients, vendors]);

  const fetchParentContracts = async () => {
    try {
      const data = await apiService.fetchData('parent-contracts');
      setParentContracts(data.data || []);
    } catch (error) {
      console.error("Error fetching parent contracts:", error);
    }
  };

  useEffect(() => {
    if (snackbar.open) {
      const timer = setTimeout(() => {
        setSnackbar({ open: false, message: '' });
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [snackbar.open]);

  useEffect(() => {
    if (!isOpen) {
      // Reset all form data and errors when modal closes
      setFormData({
        contractTitle: '',
        contractType: '',
        parent_contract_id: '',
        description: '',
        ownerDepartment: '',
        clientName: '',
        clientEmail: '',
        vendorName: '',
        vendorEmail: '',
        startDate: '',
        endDate: '',
        renewalDate: '',
        status_id: '',
        region_id: '',
        project_id: '',
        year: '',
        businessType: '',
        contract_value: '',
        currency_type: 'INR',
        subsidiary: '',
        billing_frequency: '',
        execution_type: [],
        hsn_code: '',
        tax_code: ''
      });
      setItemDetails([{ item: '', quantity: '' }]);
      setSowDetails([{ role_name: '', role_rate: '', sow_amount: '', sow_hours: '' }]);
      setErrors({});
      setSnackbarMessage('');
      setSnackbarOpen(false);
      setIsSuccess(false);
      setIsPreviewModalOpen(false);

      // Optionally clear previous validation or API error messages
      setApiError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  if (isPrefillLoading) {
    return (
      <div className="modal-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0, 0, 0, 0.4)', zIndex: 1100 }}>
        <div className="modal-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '30px', background: '#fff', borderRadius: '10px', boxShadow: '0 10px 25px rgba(0,0,0,0.15)', minWidth: '300px' }}>
          <div className="spinner" style={{ width: 40, height: 40, border: '4px solid #ffe3d6', borderTopColor: '#ff6b35', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          <p style={{ marginTop: '15px', color: '#666', fontFamily: 'Inter, sans-serif', fontSize: '14px', margin: 0 }}>Loading prefill data...</p>
        </div>
      </div>
    );
  }

  const handleInputChange = createInputChangeHandler(
    clients,
    vendors,
    setEmails,
    setVendorEmails,
    setFormData,
    setErrors
  );

  // ============ VALIDATION HELPER FUNCTIONS ============
  // All validation logic has been extracted to validationHelpers.js
  // This keeps the main component's cognitive complexity low

  const handleCloseModal = () => {
    onClose();
  };


  // ============ SUBMISSION HELPER FUNCTIONS ============
  // All payload building logic has been extracted to payloadBuilders.js
  // This keeps the main component's cognitive complexity low

  // Helper: Send email notification
  const sendContractEmail = async (contractData, templateType) => {
    const html = EmailTemplate({
      recipientName: formData.user_name || 'User',
      contractTitle: contractData.contract_title,
      contractId: contractData.contract_id || 'N/A',
      contractType: contractData.contract_type,
      clientName: contractData.partyName,
      partyLabel: formData.businessType === 'Vendor' ? 'Vendor Name' : 'Client Name',
      projectName: contractData.project_name,
      departmentName: contractData.owner_department_id,
      region: contractData.RegionName,
      status: contractData.status,
      startDate: contractData.start_date,
      endDate: contractData.end_date,
      renewalDate: contractData.renewal_date,
      creatorName: contractData.created_by_name,
      creationDate: contractData.created_at,
      contractUrl: contractData.file_url || '#',
      templateType: templateType,
    });

    try {
      await apiService.sendEmail(
        contractData.created_by_email,
        templateType === 'create-uploaded'
          ? 'New Uploaded Document Contract Created'
          : templateType === 'create-template'
            ? 'New Template-Based Contract Created'
            : 'New Blank Document Contract Created',
        html
      );
      console.log('Email sent successfully');
    } catch (emailError) {
      console.error('Failed to send email:', emailError);
      setSnackbarMessage('Contract saved, but email notification failed');
      setIsSuccess(false);
      setSnackbarOpen(true);
    }
  };

  // Helper: Handle API response
  const handleAPIResponse = async (res, templateType) => {
    if (res.status === "success") {
      // Build dynamic party info
      let partyName = 'N/A';
      let partyEmail = 'N/A';

      if (formData.businessType === 'Client') {
        const client = clients.find(c => c.client_id === Number(formData.clientName));
        partyName = client?.clientName || 'N/A';
        partyEmail = formData.clientEmail || client?.clientEmail || 'N/A';
      } else if (formData.businessType === 'Vendor') {
        const vendor = vendors.find(v => v.vendor_id === Number(formData.vendorName));
        partyName = vendor?.vendorName || 'N/A';
        partyEmail = formData.vendorEmail || vendor?.vendorEmail || 'N/A';
      }

      const contractData = {
        contract_title: formData.contractTitle,
        contract_type: getContractTypeLabel(),
        partyName,
        partyEmail,
        project_name: projects.find(p => p.id === Number(formData.project_id))?.name || 'N/A',
        owner_department_id: departments.find(d => d.id === Number(formData.ownerDepartment))?.value || 'N/A',
        RegionName: regions.find(r => r.id === Number(formData.region_id))?.value || 'N/A',
        status: statuses.find(s => s.id === Number(formData.status_id))?.value || 'Draft',
        start_date: formData.startDate,
        end_date: formData.endDate,
        renewal_date: formData.renewalDate,
        created_by_name: sessionStorage.getItem('user_name') || 'Unknown User',
        created_by_email: sessionStorage.getItem('user_emailId') || 'N/A',
        created_at: new Date().toISOString().split('T')[0],
        contract_id: res.contract_id,
        file_url: res.file_url,
      };

      await sendContractEmail(contractData, templateType);

      // Set snackbar message
      const snackbarMessage = uploadedFile ? (
        <span>
          File Uploaded successfully (ID: {res.contract_id}) <a href={res.file_url} target="_blank" rel="noopener noreferrer" style={{ color: "#fff", textDecoration: "underline" }}>click here to view the file</a>
        </span>
      ) : (
        <span>
          A new {selectedTemplate ? 'template-based' : 'blank'} doc was created (ID: {res.contract_id}) <a href={res.file_url} target="_blank" rel="noopener noreferrer" style={{ color: "#fff", textDecoration: "underline" }}>click here to open</a>
        </span>
      );

      setIsSuccess(true);
      setSnackbarOpen(true);
      setSnackbarMessage(snackbarMessage);

      if (onSave) {
        await onSave(formData, (resolve) => {
          resolve();
          handleCloseModal();
        });
      }

      setTimeout(() => {
        setSnackbarOpen(false);
        onClose();
      }, 5000);
    } else {
      const message = res.message === 'Duplicate Contract'
        ? "This contract already exists"
        : res.message || "Failed to save contract";

      setSnackbarMessage(message);
      setIsSuccess(false);
      setSnackbarOpen(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    console.log("Form submission started with data:", formData);
    const validationErrors = validateForm(
      formData,
      contractTypes,
      contractSelection,
      statuses,
      isAmendment,
      isAddendum,
      isAutoRenewal,
      uploadedFile
    );

    if (validationErrors.project_id && projectRef.current) {
      projectRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    if (Object.keys(validationErrors).length > 0) {
      console.log("Validation failed with errors:", validationErrors);
      setErrors(validationErrors);
      setIsLoading(false);
      return;
    }

    console.log("Validation passed, proceeding with API call...");

    try {
      let res;
      let templateType;

      if (uploadedFile) {
        const formDataObj = new FormData();
        const payload = buildUploadPayload(
          formData,
          userId,
          isAutoRenewal,
          parentNode,
          contractSelection,
          isAmendment,
          isAddendum,
          itemDetails,
          sowDetails
        );

        Object.entries(payload).forEach(([key, value]) => {
          if (key === 'parent_contract_id') {
            formDataObj.append(key, value !== null && value !== undefined ? value : '');
          } else if (key === 'item_details' || key === 'sow_details') {
            formDataObj.append(key, JSON.stringify(value));
          } else if (value !== null && value !== undefined) {
            formDataObj.append(key, value);
          }
        });

        formDataObj.append("file", uploadedFile);
        templateType = "create-uploaded";
        res = await apiService.postData("contracts/upload", formDataObj, true);
      } else if (selectedTemplate) {
        const payload = buildTemplatePayload(
          formData,
          userId,
          isAutoRenewal,
          parentNode,
          contractSelection,
          isAmendment,
          isAddendum,
          selectedTemplate,
          itemDetails,
          sowDetails
        );
        templateType = "create-template";
        res = await apiService.postData("contracts/template", payload);
      } else {
        const payload = buildBlankPayload(
          formData,
          userId,
          isAutoRenewal,
          parentNode,
          contractSelection,
          isAmendment,
          isAddendum,
          itemDetails,
          sowDetails
        );
        templateType = "create";
        res = await apiService.postData("contracts/blank", payload);
      }

      await handleAPIResponse(res, templateType);
    } catch (err) {
      console.error("Save error:", err);
      setSnackbarMessage("Error creating contract: " + (err.response?.data?.message || err.message));
      setIsSuccess(false);
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const submitContract = async () => {
    if (uploadedFile) {
      return await uploadContract();
    } else if (selectedTemplate) {
      return await createFromTemplate();
    } else {
      return await createBlankContract();
    }
  };

  const uploadContract = async () => {
    const formDataObj = new FormData();
    const payload = {
      contract_title: formData.contractTitle,
      owner_department_id: formData.ownerDepartment,
      start_date: formData.startDate,
      end_date: formData.endDate,
      renewal_date: formData.renewalDate || null,
      status_id: formData.status_id ? Number(formData.status_id) : null,
      contract_type: Number(formData.contractType),
      region_id: formData.region_id ? Number(formData.region_id) : null,
      project_id: formData.project_id ? Number(formData.project_id) : null,
      created_by: Number(userId),
      updated_by: Number(userId),
      creation_type: "upload",
      user_id: Number(userId),
      parent_contract_id: formData.parent_contract_id || null,
      parent_contract_type: parentNode?.data?.type === "Folder" ? 1 : 0,
      description: formData.description || "",
      is_deleted: 0,
      is_favourite: 0,
      is_metadata_required: true,
    };

    // Add existing_contract field based on radio button selection
    payload.existing_contract = contractSelection === 'existing';

    // Add year field if existing contract is selected
    if (contractSelection === 'existing' && formData.year) {
      payload.year = formData.year;
    }

    // Add type field from business type dropdown
    payload.type = formData.businessType;

    // Handle client_id and vendor_id based on business type
    if (formData.businessType === 'Client') {
      payload.client_id = Number(formData.clientName);
      payload.client_email = formData.clientEmail;
      payload.vendor_id = 0;
    } else if (formData.businessType === 'Vendor') {
      payload.vendor_id = Number(formData.vendorName);
      payload.client_id = 0;
    }

    Object.entries(payload).forEach(([key, value]) => {
      if (key === 'parent_contract_id') {
        formDataObj.append(key, value !== null && value !== undefined ? value : '');
      } else if (value !== null && value !== undefined) {
        formDataObj.append(key, value);
      }
    });

    formDataObj.append("file", uploadedFile);
    console.log("Upload flow - existing_contract:", formDataObj.get('existing_contract'));
    console.log("Upload flow - type:", formDataObj.get('type'));
    console.log("Upload flow - client_id:", formDataObj.get('client_id'));
    console.log("Upload flow - vendor_id:", formDataObj.get('vendor_id'));
    console.log("Upload flow - year:", formDataObj.get('year'));
    return await apiService.postData("contracts/upload", formDataObj, true);
  };

  const createFromTemplate = async () => {
    console.log("formData.ownerDepartment before payload:", formData.ownerDepartment);
    console.log("selectedTemplate.template_name before payload:", selectedTemplate?.template_name);
    const payload = {
      contract_title: formData.contractTitle,
      start_date: formData.startDate,
      end_date: formData.endDate,
      renewal_date: formData.renewalDate || null,
      status_id: Number(formData.status_id),
      contract_type: Number(formData.contractType),
      region_id: Number(formData.region_id),
      user_id: Number(userId),
      parent_contract_id: formData.parent_contract_id || null,
      creation_type: "template",
      template_id: selectedTemplate.template_id,
      description: formData.description || "",
      is_deleted: 0,
      is_favourite: 0,
      project_id: formData.project_id ? Number(formData.project_id) : null,
      owner_department_id: Number(formData.ownerDepartment) || 0,
      template_name: selectedTemplate?.template_name || "",
    };

    // Add existing_contract field based on radio button selection
    payload.existing_contract = contractSelection === 'existing';

    // Add year field if existing contract is selected
    if (contractSelection === 'existing' && formData.year) {
      payload.year = formData.year;
    }

    // Add type field from business type dropdown
    payload.type = formData.businessType;

    // Handle client_id and vendor_id based on business type
    if (formData.businessType === 'Client') {
      payload.client_id = Number(formData.clientName);
      payload.vendor_id = 0;
    } else if (formData.businessType === 'Vendor') {
      payload.vendor_id = Number(formData.vendorName);
      payload.client_id = 0;
    }

    console.log("Create-from-template Payload:", payload);
    console.log("Template flow - existing_contract:", payload.existing_contract);
    console.log("Template flow - type:", payload.type);
    console.log("Template flow - client_id:", payload.client_id);
    console.log("Template flow - vendor_id:", payload.vendor_id);
    console.log("Template flow - year:", payload.year);
    return await apiService.postData("contracts/template", payload);
  };

  const createBlankContract = async () => {
    const payload = {
      contract_title: formData.contractTitle,
      owner_department_id: formData.ownerDepartment,
      start_date: formData.startDate,
      end_date: formData.endDate,
      renewal_date: formData.renewalDate || null,
      status_id: formData.status_id ? Number(formData.status_id) : null,
      contract_type: Number(formData.contractType),
      region_id: formData.region_id ? Number(formData.region_id) : null,
      project_id: formData.project_id ? Number(formData.project_id) : null,
      created_by: Number(userId),
      updated_by: Number(userId),
      is_deleted: 0,
      is_favourite: 0,
      is_metadata_required: true,
      priority_template_id: 2,
      parent_contract_id: formData.parent_contract_id || null,
      description: formData.description || "",
      current_stage_id: 1,
      creation_type: "blank",
      user_id: Number(userId),
      parent_contract_type: parentNode?.data?.type === "Folder" ? 1 : 0,
    };

    // Add existing_contract field based on radio button selection
    payload.existing_contract = contractSelection === 'existing';

    // Add year field if existing contract is selected
    if (contractSelection === 'existing' && formData.year) {
      payload.year = formData.year;
    }

    // Add type field from business type dropdown
    payload.type = formData.businessType;

    // Handle client_id and vendor_id based on business type
    if (formData.businessType === 'Client') {
      payload.client_id = Number(formData.clientName);
      payload.client_email = formData.clientEmail;
      payload.vendor_id = 0;
    } else if (formData.businessType === 'Vendor') {
      payload.vendor_id = Number(formData.vendorName);
      payload.client_id = 0;
    }

    console.log("Making API call to contracts/blank with payload:", payload);
    console.log("Blank flow - existing_contract:", payload.existing_contract);
    console.log("Blank flow - type:", payload.type);
    console.log("Blank flow - client_id:", payload.client_id);
    console.log("Blank flow - vendor_id:", payload.vendor_id);
    console.log("Blank flow - year:", payload.year);
    return await apiService.postData("contracts/blank", payload);
  };

  const handleSuccessResponse = (res) => {
    console.log("API Response:", res);
    const snackbarMessage = uploadedFile ? (
      <span>
        File Uploaded successfully (ID: {res.contract_id}) <a href={res.file_url} target="_blank" rel="noopener noreferrer" style={{ color: "#fff", textDecoration: "underline" }}>click here to view the file</a>
      </span>
    ) : (
      <span>
        A new blank doc was created (ID: {res.contract_id}) <a href={res.file_url} target="_blank" rel="noopener noreferrer" style={{ color: "#fff", textDecoration: "underline" }}>click here to open</a>
      </span>
    );

    setIsSuccess(true);
    setSnackbarOpen(true);
    setSnackbarMessage(snackbarMessage);
    console.log("Snackbar set to:", { open: true, message: snackbarMessage });

    if (typeof onSave === "function" && res.contract_id) {
      onSave(formData, (resolve) => resolve());
    }

    setTimeout(() => {
      setSnackbarOpen(false);
      onClose();
    }, 5000);
  };

  const handleProjectSave = async (newProject) => {
    setProjects((prev) => [...prev, newProject]);
    console.log("New project added:", newProject);
    await fetchProjects();
  };

  const handleClientSave = async (newClient) => {
    // Optionally keep the local update for instant feedback
    setClients((prev) => [...prev, newClient]);
    setEmails((prev) => [...prev, newClient.clientEmail].filter(Boolean));
    console.log("New client added:", newClient);

    // Fetch latest clients from API
    await fetchClients();
  };

  const handleVendorSave = async (newVendor) => {
    // Optionally keep the local update for instant feedback
    setVendors((prev) => [...prev, newVendor]);
    setVendorEmails((prev) => [...prev, newVendor.vendorEmail].filter(Boolean));
    console.log("New vendor added:", newVendor);

    // Fetch latest vendors from API
    await fetchVendors();
  };

  const handlePreview = (e) => {
    e.preventDefault();
    const validationErrors = validateForm(
      formData,
      contractTypes,
      contractSelection,
      statuses,
      isAmendment,
      isAddendum,
      isAutoRenewal,
      uploadedFile
    );
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);

      // Show error message to user
      const errorCount = Object.keys(validationErrors).length;
      const errorMessage = `Please fix ${errorCount} validation error${errorCount > 1 ? 's' : ''} before previewing the contract.`;

      setSnackbarMessage(errorMessage);
      setIsSuccess(false);
      setSnackbarOpen(true);

      // Scroll to first error field
      const firstErrorField = Object.keys(validationErrors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return;
    }
    setIsPreviewModalOpen(true);
  };

  const handleCreateFromPreview = async () => {
    setIsPreviewModalOpen(false);
    await handleSubmit(new Event('submit'));
  };

  const getParentContractId = (value) => {
    // Priority 1: if value is a node object with data.id
    if (value && typeof value === "object" && value.data && value.data.id) {
      return Number(value.data.id);
    }

    // Priority 2: if value itself is a numeric string/number
    if (value && value !== "undefined" && value !== "null") {
      const numValue = Number(value);
      if (!isNaN(numValue)) return numValue;
    }

    // Priority 3: fallback to prop parentContractId
    if (parentContractId && !isNaN(Number(parentContractId))) {
      return Number(parentContractId);
    }

    // Priority 4: safe default
    return 0;
  };

  // Local wrapper to use imported function with current state
  const getContractTypeLabel = () => {
    return getSelectedContractTypeLabel(formData.contractType, contractTypes);
  };


  return (
    <div className="modal-overlay" onClick={onClose}>
      {/* Loading overlay */}
      {isLoading && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999
        }}>
          <div style={{
            background: "white",
            padding: "30px",
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)"
          }}>
            <CircularProgress size={40} style={{ color: '#ff6b35' }} />
            <span style={{ color: "#333", fontSize: "16px", fontWeight: "500" }}>Saving Contract...</span>
          </div>
        </div>
      )}
      <div
        className="modal-content create-blank-modal"
        onClick={(e) => e.stopPropagation()}
        style={isMainMaximized ? {
          maxWidth: '100vw',
          width: '100vw',
          height: '100vh',
          maxHeight: '100vh',
          borderRadius: 0,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2100,
          background: '#fff',
          overflow: 'auto',
        } : {}}
      >
        <div className="modal-header">
          <div className="header-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div className="template">
              <div className="heading-2">
                <div className="icon-box-2">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ width: '28px', height: '28px' }}
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                  </svg>
                </div>

                <div className="heading-2-content">
                  <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '600', color: '#1e293b' }}>
                    Create Contract
                  </h1>
                  <p style={{
                    margin: '8px 0 0 0',
                    fontSize: '12px',
                    color: '#64748b',
                    fontWeight: '400',
                    lineHeight: '1.5'
                  }}>
                    Draft and manage your legal agreements
                  </p>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                title={isMainMaximized ? 'Minimize' : 'Maximize'}
                onClick={() => setIsMainMaximized((v) => !v)}
                style={{
                  color: 'white',
                  fontSize: '22px',
                  border: 'none',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  marginRight: '50px',
                  background: 'transparent',
                  marginTop: '-45px'

                }}
              >
                {isMainMaximized ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="24" height="24" fill="#ff6b35" rx="4" />
                    <defs>
                      <filter id="glowMin" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                        <feMerge>
                          <feMergeNode in="coloredBlur" />
                          <feMergeNode in="SourceGraphic" />
                        </feMerge>
                      </filter>
                    </defs>
                    <path d="M9 9V4.5M9 9H4.5M9 9L3.5 3.5M15 9h4.5M15 9V4.5M15 9l5.5-5.5M9 15v4.5M9 15H4.5M9 15l-5.5 5.5M15 15h4.5M15 15v4.5m0-4.5l5.5 5.5"
                      stroke="white" stroke-width="1.5" fill="none" filter="url(#glowMin)" />
                  </svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="24" height="24" fill="#ff6b35" rx="4" />
                    <defs>
                      <filter id="glowMax" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                        <feMerge>
                          <feMergeNode in="coloredBlur" />
                          <feMergeNode in="SourceGraphic" />
                        </feMerge>
                      </filter>
                    </defs>
                    <path d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                      stroke="white" stroke-width="1.5" fill="none" filter="url(#glowMax)" />
                  </svg>
                )}
              </button>
              <button className="close-btn" onClick={onClose} style={{ color: 'white', fontSize: '28px', border: 'none', borderRadius: '50%', width: '35px', height: '35px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease' }}>×</button>
            </div>
          </div>
          {/* Radio buttons - only show for upload flow */}
          {uploadedFile && (
            <div className="contract-type-selection">
              <label>
                <input
                  type="radio"
                  name="contractTypeSelection"
                  value="new"
                  checked={contractSelection === 'new'}
                  onChange={(e) => setContractSelection(e.target.value)}
                />
                New Contract
              </label>
              <label>
                <input
                  type="radio"
                  name="contractTypeSelection"
                  value="existing"
                  checked={contractSelection === 'existing'}
                  onChange={(e) => setContractSelection(e.target.value)}
                />
                Existing Contract
              </label>
            </div>
          )}
        </div>


        <div className="form-container">
          {apiError && (
            <div className="error-message">
              {apiError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="modal-form">
            <div className="form-section">
              <div className="form-group">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <label style={{ flex: 1 }}>
                    Contract Title <span className="required-asterisk">*</span>
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '14px', color: '#ff6b35', fontWeight: '500' }}>
                      Is Addendum
                    </span>
                    <div
                      style={{
                        width: '40px',
                        height: '20px',
                        backgroundColor: isAddendum ? '#ff6b35' : '#fff',
                        border: '2px solid #ff6b35',
                        borderRadius: '20px',
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'background-color 0.3s'
                      }}
                      onClick={() => setIsAddendum(!isAddendum)}
                    >
                      <div
                        style={{
                          width: '16px',
                          height: '16px',
                          backgroundColor: isAddendum ? '#fff' : '#ff6b35',
                          borderRadius: '50%',
                          position: 'absolute',
                          top: '1px',
                          left: isAddendum ? '20px' : '2px',
                          transition: 'left 0.3s, background-color 0.3s'
                        }}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '14px', color: '#ff6b35', fontWeight: '500' }}>
                      Is Amendment
                    </span>

                    <div
                      style={{
                        width: '40px',
                        height: '20px',
                        backgroundColor: isAmendment ? '#ff6b35' : '#fff',
                        border: '2px solid #ff6b35',
                        borderRadius: '20px',
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'background-color 0.3s'
                      }}
                      onClick={() => setIsAmendment(!isAmendment)}
                    >
                      <div
                        style={{
                          width: '16px',
                          height: '16px',
                          backgroundColor: isAmendment ? '#fff' : '#ff6b35',
                          borderRadius: '50%',
                          position: 'absolute',
                          top: '1px',
                          left: isAmendment ? '20px' : '2px',
                          transition: 'left 0.3s, background-color 0.3s'
                        }}
                      />
                    </div>
                  </div>

                </div>
                <input
                  type="text"
                  name="contractTitle"
                  value={formData.contractTitle}
                  onChange={handleInputChange}
                  placeholder="Enter contract title"
                />
                {errors.contractTitle && (
                  <span className="error">{errors.contractTitle}</span>
                )}
              </div>

              <div className="form-group">
                <label>
                  Business Type <span className="required-asterisk">*</span>
                </label>
                <div className="dropdown-container">
                  <CustomSelect
                    className="business-type-select"
                    options={[
                      { label: "Client", value: "Client" },
                      { label: "Vendor", value: "Vendor" },
                    ]}
                    value={formData.businessType || ""}
                    placeholder="Select Business Type"
                    onChange={(val) => {
                      console.log("Selected Business Type:", val);
                      setFormData((prev) => ({ ...prev, businessType: val }));
                      setErrors((prev) => ({ ...prev, businessType: "" }));
                    }}
                    renderAsSpan={true}
                  />
                </div>
                {errors.businessType && (
                  <span className="error">{errors.businessType}</span>
                )}
              </div>

              <div className="form-group">
                <label>
                  Contract Type <span className="required-asterisk">*</span>
                </label>
                <div className="dropdown-container">
                  <CustomSelect
                    className="contract-type-select"
                    options={Array.isArray(contractTypes) ? contractTypes.map(ct => ({
                      label: ct.value,      // 👀 display value
                      value: String(ct.id || ct.value) // ✅ use id if available, else value
                    })) : []}
                    value={String(formData.contractType || "")}
                    placeholder="Select Contract Type"
                    onChange={(val) => {
                      console.log("Selected Contract Type:", val);
                      setFormData(prev => ({ ...prev, contractType: val }));
                      setErrors(prev => ({ ...prev, contractType: "" }));
                    }}
                    renderAsSpan={true}
                  />
                </div>
                {errors.contractType && (
                  <span className="error">{errors.contractType}</span>
                )}
              </div>

              {contractSelection === 'existing' && (
                <div className="form-group">
                  <label>
                    Year <span className="required-asterisk">*</span>
                  </label>
                  <div className="dropdown-container">
                    <CustomSelect
                      className="year-select"
                      options={generateYears()}
                      value={formData.year || ""}
                      placeholder="Select Year"
                      onChange={(val) => {
                        console.log("Selected Year:", val);
                        setFormData(prev => ({ ...prev, year: val }));
                        setErrors(prev => ({ ...prev, year: "" }));
                      }}
                      renderAsSpan={true}
                    />
                  </div>
                  {errors.year && <span className="error">{errors.year}</span>}
                </div>
              )}


              {(isAmendment || isAddendum || !(
                getContractTypeLabel().includes("MSA") ||
                getContractTypeLabel().includes("NDA")
              )) && (
                  <div className="form-group">
                    <label>Parent Contract <span className="required-asterisk">*</span></label>
                    <div className="dropdown-container">
                      <CustomSelect
                        className="parent-contract-select"
                        options={Array.isArray(parentContracts) ? parentContracts.map(pc => ({
                          label: pc.contract_title,
                          value: String(pc.contract_id)
                        })) : []}
                        value={String(formData.parent_contract_id || "")}
                        placeholder="Select Parent Contract"
                        onChange={(val) => {
                          setFormData(prev => ({ ...prev, parent_contract_id: val }));
                        }}
                        renderAsSpan={true}
                      />
                    </div>
                    {errors.parent_contract_id && (
                      <span className="error">{errors.parent_contract_id}</span>
                    )}
                  </div>
                )}

              <div className="form-group">
                <label>
                  Owner Department <span className="required-asterisk">*</span>
                </label>
                <div className="dropdown-container">
                  <CustomSelect
                    className="owner-department"
                    options={Array.isArray(departments) ? departments.map(dep => ({
                      label: dep.value,   // what user sees
                      value: dep.id       // what we store/send
                    })) : []}
                    value={formData.ownerDepartment}
                    placeholder="Select Owner Department"
                    onChange={(val) => {
                      setFormData(prev => ({ ...prev, ownerDepartment: val }));
                      setErrors(prev => ({ ...prev, ownerDepartment: "" }));
                    }}
                    renderAsSpan={true}
                  />

                </div>
                {errors.ownerDepartment && <span className="error">{errors.ownerDepartment}</span>}
              </div>

              <div className="form-group">
                <label>
                  Status <span className="required-asterisk">*</span>
                </label>
                <div className="dropdown-container">
                  <CustomSelect
                    className="status-select"
                    options={Array.isArray(statuses) ? statuses.map(st => ({
                      label: st.value,
                      value: String(st.id || st.value)
                    })) : []}
                    value={String(formData.status_id || "")}
                    placeholder="Select Status"
                    onChange={(val) => {
                      console.log("Selected Status ID:", val);
                      setFormData(prev => ({ ...prev, status_id: val }));
                      setErrors(prev => {
                        const newErrors = { ...prev, status_id: "" };
                        const sel = Array.isArray(statuses) ? statuses.find(s => String(s.id) === String(val) || String(s.value) === String(val)) : null;
                        if (sel?.value?.trim().toLowerCase() === 'completed') {
                          if (newErrors.renewalDate === 'Please select a renewal date') {
                            newErrors.renewalDate = "";
                          }
                        }
                        return newErrors;
                      });
                    }}
                    renderAsSpan={true}
                  />


                </div>
                {errors.status_id && <span className="error">{errors.status_id}</span>}
              </div>





            </div>
            {/* Client Fields - Show when business type is Client */}
            {formData.businessType === 'Client' && (
              <>
                <span
                  className="add-icon-client"
                  title="Add Client"
                  onClick={() => setIsClientPopupOpen(true)}
                >
                  +
                </span>
                <div className="form-section-row contact-section">
                  <div className="form-group">
                    <label>
                      Client Name <span className="required-asterisk">*</span>
                    </label>
                    <div className="dropdown-container">
                      <div className="dropdown-container">
                        <CustomSelect
                          className="client-name"
                          options={Array.isArray(clients) ? clients.map(c => ({
                            label: c.clientName,   // Display clientName from API
                            value: c.client_id     // Use client_id as value
                          })) : []}
                          value={formData.clientName}  // Use clientName (stores client_id)
                          placeholder="Select Client Name"
                          onChange={(val) => {
                            const selectedClient = Array.isArray(clients) ? clients.find(c => c.client_id === val) : null;
                            const clientEmails = [
                              selectedClient?.clientEmail,
                              selectedClient?.salesRepresentativeEmail
                            ].filter(Boolean);

                            setEmails(clientEmails);

                            setFormData(prev => ({
                              ...prev,
                              clientName: val,                   // Store client_id in clientName
                              clientEmail: clientEmails[0] || "" // Auto-pick first email if available
                            }));

                            setErrors(prev => ({ ...prev, clientName: "" }));
                          }}
                          renderAsSpan={true}
                        />
                      </div>
                    </div>
                    {errors.clientName && <span className="error">{errors.clientName}</span>}
                  </div>

                  <div className="form-group">
                    <label>
                      Client Email
                    </label>
                    <div className="dropdown-container">
                      <CustomSelect
                        className="client-email"
                        options={Array.isArray(emails) && emails.length > 0 ? emails.map(email => ({
                          label: email,
                          value: email
                        })) : [{ label: "No email available", value: "" }]}
                        value={formData.clientEmail || ""}
                        placeholder="Select Client Email"
                        onChange={(val) => {
                          setFormData(prev => ({
                            ...prev,
                            clientEmail: val // Update clientEmail with selected email
                          }));
                          setErrors(prev => ({ ...prev, clientEmail: "" }));
                        }}
                        renderAsSpan={true}
                      />
                    </div>
                    {errors.clientEmail && <span className="error">{errors.clientEmail}</span>}
                  </div>
                </div>
              </>
            )}

            {/* Vendor Fields - Show when business type is Vendor */}
            {formData.businessType === 'Vendor' && (
              <>
                <span
                  className="add-icon-client"
                  title="Add Vendor"
                  onClick={() => setIsVendorPopupOpen(true)}
                >
                  +
                </span>
                <div className="form-section-row contact-section">
                  <div className="form-group">
                    <label>
                      Vendor Name <span className="required-asterisk">*</span>
                    </label>
                    <div className="dropdown-container">
                      <div className="dropdown-container">
                        <CustomSelect
                          className="vendor-name"
                          options={Array.isArray(vendors) ? vendors.map(v => ({
                            label: v.vendorName,   // Display vendorName from API
                            value: v.vendor_id     // Use vendor_id as value
                          })) : []}
                          value={formData.vendorName}  // Use vendorName (stores vendor_id)
                          placeholder="Select Vendor Name"
                          onChange={(val) => {
                            const selectedVendor = Array.isArray(vendors) ? vendors.find(v => v.vendor_id === val) : null;
                            const vendorEmails = [
                              selectedVendor?.vendorEmail
                            ].filter(Boolean);

                            setVendorEmails(vendorEmails);

                            setFormData(prev => ({
                              ...prev,
                              vendorName: val,                   // Store vendor_id in vendorName
                              vendorEmail: vendorEmails[0] || "" // Auto-pick first email if available
                            }));

                            setErrors(prev => ({ ...prev, vendorName: "" }));
                          }}
                          renderAsSpan={true}
                        />
                      </div>
                    </div>
                    {errors.vendorName && <span className="error">{errors.vendorName}</span>}
                  </div>

                  <div className="form-group">
                    <label>
                      Vendor Email <span className="required-asterisk">*</span>
                    </label>
                    <div className="dropdown-container">
                      <CustomSelect
                        className="vendor-email"
                        options={Array.isArray(vendorEmails) && vendorEmails.length > 0 ? vendorEmails.map(email => ({
                          label: email,
                          value: email
                        })) : [{ label: "No email available", value: "" }]}
                        value={formData.vendorEmail || ""}
                        placeholder="Select Vendor Email"
                        onChange={(val) => {
                          setFormData(prev => ({
                            ...prev,
                            vendorEmail: val
                          }));
                          setErrors(prev => ({ ...prev, vendorEmail: "" }));
                        }}
                        renderAsSpan={true}
                      />
                    </div>
                    {errors.vendorEmail && <span className="error">{errors.vendorEmail}</span>}
                  </div>
                </div>
              </>
            )}
            <div className="form-section">
              <div className="form-group" ref={projectRef}>
                <div className="label-row">
                  <label>
                    Projects <span className="required-asterisk">*</span>
                  </label>
                  <span
                    className="add-icon"
                    title="Add Project"
                    // onClick={() => setIsProjectPopupOpen(true)}
                    onClick={() => {
                      const selectedClient = clients.find(c => String(c.client_id) === String(formData.clientName));
                      const prefix = selectedClient ? selectedClient.clientName + '-' : '';
                      setProjectPopupPrefix(prefix); // Add this state
                      setIsProjectPopupOpen(true);
                    }}
                  >
                    +
                  </span>
                </div>
                <div className="dropdown-container">
                  <CustomSelect
                    className="projects-select"
                    options={Array.isArray(projects) ? projects
                      .filter(p => p.name)
                      .map(p => ({
                        label: p.name,
                        value: String(p.id),
                      })) : []}
                    value={String(formData.project_id || "")}
                    placeholder="Select Project"
                    onChange={(val) => {
                      console.log("Selected Project ID:", val);
                      setFormData(prev => ({ ...prev, project_id: val }));
                      setErrors(prev => ({ ...prev, project_id: "" }));
                    }}
                    renderAsSpan={true}
                  />
                </div>
                {errors.project_id && (
                  <div className="error" style={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                    {errors.project_id}
                  </div>
                )}
              </div>
            </div>
            <div className="form-section">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginLeft: 'auto' }}>
                <span style={{ fontSize: '14px', color: '#ff6b35', fontWeight: '500' }}>Auto Renewal</span>
                <div
                  style={{
                    width: '40px',
                    height: '20px',
                    backgroundColor: isAutoRenewal ? '#ff6b35' : '#fff',
                    border: '2px solid #ff6b35',
                    borderRadius: '20px',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s'
                  }}
                  // onClick={() => setIsAutoRenewal(!isAutoRenewal)}
                  onClick={() => {
                    const newAutoRenewalState = !isAutoRenewal;
                    setIsAutoRenewal(newAutoRenewalState);
                    if (newAutoRenewalState) {
                      setFormData(prev => ({
                        ...prev,
                        renewalDate: ''
                      }));
                    }
                  }}
                >
                  <div
                    style={{
                      width: '16px',
                      height: '16px',
                      backgroundColor: isAutoRenewal ? '#fff' : '#ff6b35',
                      borderRadius: '50%',
                      position: 'absolute',
                      top: '1px',
                      left: isAutoRenewal ? '20px' : '2px',
                      transition: 'left 0.3s, background-color 0.3s'
                    }}
                  />
                </div>
              </div>
              <div className="blank-form-row">

                <div className="form-group">
                  <label>
                    Start Date <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}

                  />
                  {errors.startDate && <span className="error">{errors.startDate}</span>}
                </div>

                <div className="form-group">
                  <label>
                    End Date <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                  />
                  {errors.endDate && <span className="error">{errors.endDate}</span>}
                </div>
              </div>
              {(!isEndDatePast || (!isAutoRenewal && formData.status_id && statuses.find(s => s.id === Number(formData.status_id))?.value === "Terminated")) && (
                <div className="blank-form-row">
                  {!isEndDatePast && (
                    <div className="form-group">
                      <label>
                        Renewal Date {!isRenewalDateOptional && <span className="required-asterisk">*</span>}
                      </label>
                      {isAutoRenewal ? (
                        <input
                          type="text"
                          value="Auto renewal"
                          disabled
                          style={{ cursor: 'not-allowed', backgroundColor: '#f5f5f5' }}
                        />
                      ) : (
                        <input
                          type="date"
                          name="renewalDate"
                          value={formData.renewalDate}
                          onChange={handleInputChange}
                        />
                      )}
                      {errors.renewalDate && (
                        <div className="error" style={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                          {errors.renewalDate}
                        </div>
                      )}
                    </div>
                  )}

                  {(
                    !isAutoRenewal &&
                    formData.status_id &&
                    statuses.find(s => s.id === Number(formData.status_id))?.value === "Terminated"
                  ) && (
                      <div className="form-group">
                        <label>
                          Termination Date <span className="required">*</span>
                        </label>
                        <input
                          type="date"
                          name="termination_date"
                          value={formData.termination_date}
                          onChange={handleInputChange}
                          className={errors.termination_date ? "input-error" : ""}
                        />
                        {errors.termination_date && (
                          <span className="error">{errors.termination_date}</span>
                        )}
                      </div>
                    )}

                </div>
              )}
            </div>

            <div className="form-section">



              <div className="form-group">
                <label>
                  Region <span className="required-asterisk">*</span>
                </label>
                <div className="dropdown-container">
                  <CustomSelect
                    className="region-select"
                    options={Array.isArray(regions) ? regions.map(r => ({
                      label: r.value,
                      value: String(r.id || r.value)
                    })) : []}
                    value={String(formData.region_id || "")}
                    placeholder="Select Region"
                    onChange={(val) => {
                      console.log("Selected Region ID:", val);
                      setFormData(prev => ({ ...prev, region_id: val }));
                      setErrors(prev => ({ ...prev, region_id: "" }));
                    }}
                    renderAsSpan={true}
                  />
                </div>
                {errors.region_id && <span className="error">{errors.region_id}</span>}
              </div>
              {/* Practices */}
              <div className="form-group">
                <label>Practices</label>
                <CustomSelect
                  options={practices}
                  value={formData.practices}
                  onChange={(val) => handleInputChange({ target: { name: "practices", value: val } })}
                  placeholder="Select Practice"
                />
              </div>

              {/* Billing Type */}
              <div className="form-group">
                <label>Billing Type</label>
                <CustomSelect
                  options={[
                    { label: "Fixed", value: "fixed" },
                    { label: "Time & Material", value: "time-n-material" },
                    { label: "Milestone Based", value: "milestone-based" },
                    { label: "Fixed Monthly", value: "fixed-monthly" }
                  ]}
                  value={formData.billing_type}
                  onChange={(val) => handleInputChange({ target: { name: "billing_type", value: val } })}
                  placeholder="Select Billing Type"
                />
              </div>

              {/* Billing Frequency */}
              <div className="form-group">
                <label>Billing Frequency</label>
                <CustomSelect
                  options={billingFrequencies}
                  value={formData.billing_frequency}
                  onChange={(val) => handleInputChange({ target: { name: "billing_frequency", value: val } })}
                  placeholder="Select Billing Frequency"
                />
              </div>

              {/* PO Number - Only for Purchase Order */}
              {getContractTypeLabel().includes("Purchase Order") && (
                <div className="form-group">
                  <label>PO Number <span className="required">*</span></label>
                  <input
                    type="text"
                    name="po_number"
                    value={formData.po_number}
                    onChange={handleInputChange}
                    placeholder="Enter PO Number"
                  />
                  {errors.po_number && <span className="error">{errors.po_number}</span>}
                </div>
              )}

              {/* Net Term */}
              <div className="form-group">
                <label>Net Term</label>
                <CustomSelect
                  options={netTerms}
                  value={formData.net_term}
                  onChange={(val) => handleInputChange({ target: { name: "net_term", value: val } })}
                  placeholder="Select Net Term"
                />
              </div>

              {/* Bill Rate Currency */}
              <div className="form-group">
                <label>Bill Rate Currency</label>
                <CustomSelect
                  options={currencies}
                  value={formData.bill_rate_currency}
                  onChange={(val) => handleInputChange({ target: { name: "bill_rate_currency", value: val } })}
                  placeholder="Select Currency"
                />
              </div>

              {/* Billing Country */}
              <div className="form-group">
                <label>Billing Country</label>
                <CustomSelect
                  options={regions.map(r => ({ label: r.value, value: r.id }))}
                  value={formData.billing_country}
                  onChange={(val) => handleInputChange({ target: { name: "billing_country", value: val } })}
                  placeholder="Select Country"
                />
              </div>

              {/* HSN and Tax Code */}
              <div className="blank-form-row">
                <div className="form-group">
                  <label>HSN Code</label>
                  <input
                    type="text"
                    name="hsn_code"
                    value={formData.hsn_code}
                    onChange={handleInputChange}
                    placeholder="Enter HSN Code"
                  />
                </div>
                <div className="form-group">
                  <label>Tax Code</label>
                  <input
                    type="text"
                    name="tax_code"
                    value={formData.tax_code}
                    onChange={handleInputChange}
                    placeholder="Enter Tax Code"
                  />
                </div>
              </div>
              {/* ---- Termination Date: show when Auto-Renewal OFF OR status = Terminated ---- */}


              {/* Termination Reason */}
              {formData.status_id && statuses.find(s => s.id === Number(formData.status_id))?.value === "Terminated" && (
                <div className="form-group">
                  <label>Termination Reason <span className="required">*</span></label>
                  <textarea
                    name="termination_reason"
                    value={formData.termination_reason}
                    onChange={handleInputChange}
                    placeholder="Enter reason"
                    rows="3"
                  />
                  {errors.termination_reason && <span className="error">{errors.termination_reason}</span>}
                </div>
              )}
              <div className="form-group">
                <label>
                  Description / Summary <span className="required-asterisk">*</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter description or summary"
                />
                {errors.description && (
                  <div className="error" style={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                    {errors.description}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>
                  Metatag
                </label>
                <input
                  type="text"
                  name="metatag"
                  value={formData.metatag}
                  onChange={handleInputChange}
                  placeholder="Enter metatag (optional)"
                />
              </div>

              <div className="form-group">
                <label>Contract Value</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <div style={{ width: '100px' }}>
                    <CustomSelect
                      options={[
                        { label: 'INR', value: 'INR' },
                        { label: 'USD', value: 'USD' },
                        { label: 'PHP', value: 'PHP' },
                        { label: 'AUD', value: 'AUD' }
                      ]}
                      value={formData.currency_type}
                      onChange={(val) => handleInputChange({ target: { name: "currency_type", value: val } })}
                      placeholder="INR"
                    />
                  </div>
                  <input
                    type="number"
                    name="contract_value"
                    value={formData.contract_value}
                    onChange={handleInputChange}
                    placeholder="Enter value"
                    style={{ flex: 1 }}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Subsidiary</label>
                <CustomSelect
                  options={subsidiaries}
                  value={formData.subsidiary}
                  onChange={(val) => handleInputChange({ target: { name: "subsidiary", value: val } })}
                  placeholder="Select Subsidiary"
                />
              </div>

              {/* Item Details Section */}
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label style={{ marginBottom: '12px', display: 'block', fontWeight: '600' }}>Item Details</label>
                {itemDetails.map((detail, index) => {
                  return (
                    <div key={index} style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 70px',
                      gap: '12px',
                      marginBottom: '12px',
                      padding: '16px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      backgroundColor: '#f8fafc',
                      alignItems: 'end'
                    }}>
                      <div>
                        <label style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', display: 'block', fontWeight: '500' }}>Item</label>
                        <input
                          type="text"
                          value={detail.item}
                          onChange={(e) => {
                            const newDetails = [...itemDetails];
                            newDetails[index].item = e.target.value;
                            setItemDetails(newDetails);
                          }}
                          placeholder="Enter Item"
                          style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', marginBottom: '0px', marginTop: '0px' }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', display: 'block', fontWeight: '500' }}>Quantity</label>
                        <input
                          type="number"
                          value={detail.quantity}
                          onChange={(e) => {
                            const newDetails = [...itemDetails];
                            newDetails[index].quantity = e.target.value;
                            setItemDetails(newDetails);
                          }}
                          placeholder="Enter Quantity"
                          min="0"
                          style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', marginBottom: '0px', marginTop: '0px' }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        {index === itemDetails.length - 1 && (
                          <button
                            type="button"
                            onClick={() => setItemDetails([...itemDetails, { item: '', quantity: '' }])}
                            style={{
                              backgroundColor: '#f8fafc',
                              color: '#475569',
                              border: '1px solid #cbd5e1',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '16px',
                              fontWeight: '500',
                              width: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.backgroundColor = '#e2e8f0';
                              e.currentTarget.style.borderColor = '#94a3b8';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8fafc';
                              e.currentTarget.style.borderColor = '#cbd5e1';
                            }}
                            title="Add Row"
                          >
                            +
                          </button>
                        )}
                        {itemDetails.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const newDetails = itemDetails.filter((_, i) => i !== index);
                              setItemDetails(newDetails);
                            }}
                            style={{
                              backgroundColor: '#f8fafc',
                              color: '#475569',
                              border: '1px solid #cbd5e1',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '16px',
                              fontWeight: '500',
                              width: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.backgroundColor = '#e2e8f0';
                              e.currentTarget.style.borderColor = '#94a3b8';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8fafc';
                              e.currentTarget.style.borderColor = '#cbd5e1';
                            }}
                            title="Remove Row"
                          >
                            −
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Execution Type */}
              <div className="form-group">
                <label>Execution Type</label>
                <CustomSelect
                  options={executionTypes}
                  value={formData.execution_type || []}
                  onChange={(val) => handleInputChange({ target: { name: "execution_type", value: val } })}
                  placeholder="Select Execution Type"
                  multiSelect={true}
                />
              </div>

              {(getContractTypeLabel().includes("SOW") || getContractTypeLabel().includes("MSA")) && (
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label style={{ marginBottom: '12px', display: 'block', fontWeight: '600' }}>SOW Details</label>
                  {sowDetails.map((detail, index) => (
                    <div key={index} style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr 1fr 70px',
                      gap: '12px',
                      marginBottom: '12px',
                      padding: '16px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      backgroundColor: '#f8fafc',
                      alignItems: 'end'
                    }}>
                      <div>
                        <label style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', display: 'block', fontWeight: '500', }}>Role Name</label>
                        <input
                          type="text"
                          value={detail.role_name}
                          onChange={(e) => {
                            const newDetails = [...sowDetails];
                            newDetails[index].role_name = e.target.value;
                            setSowDetails(newDetails);
                          }}
                          placeholder="Enter Role"
                          style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', marginBottom: '0px', marginTop: '0px' }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', display: 'block', fontWeight: '500' }}>Role Rate</label>
                        <input
                          type="number"
                          value={detail.role_rate}
                          onChange={(e) => {
                            const newDetails = [...sowDetails];
                            newDetails[index].role_rate = e.target.value;
                            setSowDetails(newDetails);
                          }}
                          placeholder="Enter Rate"
                          style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', display: 'block', fontWeight: '500' }}>SOW Amount</label>
                        <input
                          type="number"
                          value={detail.sow_amount}
                          onChange={(e) => {
                            const newDetails = [...sowDetails];
                            newDetails[index].sow_amount = e.target.value;
                            setSowDetails(newDetails);
                          }}
                          placeholder="Enter Amount"
                          style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', display: 'block', fontWeight: '500' }}>SOW Hours</label>
                        <input
                          type="number"
                          value={detail.sow_hours}
                          onChange={(e) => {
                            const newDetails = [...sowDetails];
                            newDetails[index].sow_hours = e.target.value;
                            setSowDetails(newDetails);
                          }}
                          placeholder="Enter Hours"
                          style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        {index === sowDetails.length - 1 && (
                          <button
                            type="button"
                            onClick={() => setSowDetails([...sowDetails, { role_name: '', role_rate: '', sow_amount: '', sow_hours: '' }])}
                            style={{
                              backgroundColor: '#f8fafc',
                              color: '#475569',
                              border: '1px solid #cbd5e1',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '16px',
                              fontWeight: '500',
                              width: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.backgroundColor = '#e2e8f0';
                              e.currentTarget.style.borderColor = '#94a3b8';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8fafc';
                              e.currentTarget.style.borderColor = '#cbd5e1';
                            }}
                            title="Add Row"
                          >
                            +
                          </button>
                        )}
                        {sowDetails.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const newDetails = sowDetails.filter((_, i) => i !== index);
                              setSowDetails(newDetails);
                            }}
                            style={{
                              backgroundColor: '#f8fafc',
                              color: '#475569',
                              border: '1px solid #cbd5e1',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '16px',
                              fontWeight: '500',
                              width: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.backgroundColor = '#e2e8f0';
                              e.currentTarget.style.borderColor = '#94a3b8';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8fafc';
                              e.currentTarget.style.borderColor = '#cbd5e1';
                            }}
                            title="Remove Row"
                          >
                            −
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

            </div>
          </form>

        </div>


        <div className="form-actions">
          <button
            type="button"
            className="modal-action-btn modal-action-btn-save"
            onClick={handlePreview}
            disabled={isLoading}
          >
            Preview
          </button>
          <button
            type="button"
            className="modal-action-btn modal-action-btn-cancel"
            onClick={() => {
              setErrors({});
              setFormData({
                contractTitle: '',
                contractType: '',
                parent_contract_id: '',
                description: '',
                ownerDepartment: '',
                clientName: '',
                clientEmail: '',
                vendorName: '',
                vendorEmail: '',
                startDate: '',
                endDate: '',
                renewalDate: '',
                status_id: '',
                region_id: '',
                project_id: '',
                year: '',
                businessType: '',
                contract_value: '',
                currency_type: 'INR'
              });
              onClose();
            }}
          >
            Cancel
          </button>

        </div>

        <ProjectPopup
          isOpen={isProjectPopupOpen}
          onClose={() => {
            setIsProjectPopupOpen(false);
            setProjectPopupPrefix('');
          }}
          onSave={handleProjectSave}
          codePrefix={projectPopupPrefix}
        />
        <ClientPopup
          isOpen={isClientPopupOpen}
          onClose={() => setIsClientPopupOpen(false)}
          onSave={handleClientSave}
        />
        <VendorPopup
          isOpen={isVendorPopupOpen}
          onClose={() => setIsVendorPopupOpen(false)}
          onSave={handleVendorSave}
        />

        {/* Preview Modal */}
        {isPreviewModalOpen && (
          <div className="modal-overlay" style={{ zIndex: 2000 }}>
            <div className="modal-content" style={{
              maxWidth: isPreviewMaximized ? '100vw' : '900px',
              width: isPreviewMaximized ? '100vw' : '95%',
              height: isPreviewMaximized ? '100vh' : 'auto',
              maxHeight: isPreviewMaximized ? '100vh' : '95vh',
              overflow: 'auto',
              backgroundColor: '#ffffff',
              border: 'none',
              borderRadius: isPreviewMaximized ? '0' : '20px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              position: isPreviewMaximized ? 'fixed' : 'relative',
              top: isPreviewMaximized ? 0 : undefined,
              left: isPreviewMaximized ? 0 : undefined,
              right: isPreviewMaximized ? 0 : undefined,
              bottom: isPreviewMaximized ? 0 : undefined,
              zIndex: 2100
            }} onClick={(e) => e.stopPropagation()}>

              {/* Header */}
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: '30px',
                borderRadius: '20px 20px 0 0',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '-50px',
                  right: '-50px',
                  width: '100px',
                  height: '100px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '50%'
                }}></div>
                <div style={{
                  position: 'absolute',
                  bottom: '-30px',
                  left: '-30px',
                  width: '60px',
                  height: '60px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '50%'
                }}></div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <h2 style={{ margin: 0, fontSize: '28px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '12px' }}>
                      📋 Contract Preview
                    </h2>
                    <p style={{ margin: '8px 0 0 0', opacity: 0.9, fontSize: '16px' }}>
                      Review your contract details before creating
                    </p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      title={isPreviewMaximized ? 'Minimize' : 'Maximize'}
                      onClick={() => setIsPreviewMaximized((v) => !v)}
                      style={{
                        color: 'white',
                        fontSize: '22px',
                        background: 'rgba(255, 255, 255, 0.2)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '36px',
                        height: '36px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '60px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                      onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                    >
                      {isPreviewMaximized ? (
                        <span style={{ fontSize: '22px' }}>🗗</span>
                      ) : (
                        <span style={{ fontSize: '22px' }}>🗖</span>
                      )}
                    </button>
                    <button
                      className="close-btn"
                      onClick={() => setIsPreviewModalOpen(false)}
                      style={{
                        color: 'white',
                        fontSize: '28px',
                        background: 'rgba(255, 255, 255, 0.2)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '40px',
                        height: '40px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                      onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>

              <div style={{ padding: '30px' }}>

                {/* Contract Details Section */}
                <div style={{
                  marginBottom: '30px',
                  padding: '25px',
                  backgroundColor: '#f8fafc',
                  borderRadius: '15px',
                  border: '1px solid #e2e8f0'
                }}>
                  <h3 style={{
                    margin: '0 0 20px 0',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#1e293b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    📄 Contract Details
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '20px'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Contract Title
                      </span>
                      <div style={{
                        padding: '12px 16px',
                        backgroundColor: 'white',
                        borderRadius: '10px',
                        border: '1px solid #e2e8f0',
                        fontSize: '16px',
                        color: '#1e293b',
                        fontWeight: '500'
                      }}>
                        {formData.contractTitle}
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Business Type
                      </span>
                      <div style={{
                        padding: '12px 16px',
                        backgroundColor: 'white',
                        borderRadius: '10px',
                        border: '1px solid #e2e8f0',
                        fontSize: '16px',
                        color: '#1e293b',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        {formData.businessType === 'Client' ? '👥' : '🏢'} {formData.businessType}
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Contract Type
                      </span>
                      <div style={{
                        padding: '12px 16px',
                        backgroundColor: 'white',
                        borderRadius: '10px',
                        border: '1px solid #e2e8f0',
                        fontSize: '16px',
                        color: '#1e293b',
                        fontWeight: '500'
                      }}>
                        {Array.isArray(contractTypes) ? contractTypes.find(ct => String(ct.id) === String(formData.contractType))?.value || formData.contractType : formData.contractType}
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Owner Department
                      </span>
                      <div style={{
                        padding: '12px 16px',
                        backgroundColor: 'white',
                        borderRadius: '10px',
                        border: '1px solid #e2e8f0',
                        fontSize: '16px',
                        color: '#1e293b',
                        fontWeight: '500'
                      }}>
                        🏛️ {Array.isArray(departments) ? departments.find(d => d.id === formData.ownerDepartment)?.value || formData.ownerDepartment : formData.ownerDepartment}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Information Section */}
                {(formData.businessType === 'Client' || formData.businessType === 'Vendor') && (
                  <div style={{
                    marginBottom: '30px',
                    padding: '25px',
                    backgroundColor: '#f0f9ff',
                    borderRadius: '15px',
                    border: '1px solid #bae6fd'
                  }}>
                    <h3 style={{
                      margin: '0 0 20px 0',
                      fontSize: '20px',
                      fontWeight: '600',
                      color: '#0c4a6e',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      {formData.businessType === 'Client' ? '👥' : '🏢'} {formData.businessType} Information
                    </h3>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                      gap: '20px'
                    }}>
                      {formData.businessType === 'Client' && (
                        <>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Client Name
                            </span>
                            <div style={{
                              padding: '12px 16px',
                              backgroundColor: 'white',
                              borderRadius: '10px',
                              border: '1px solid #e2e8f0',
                              fontSize: '16px',
                              color: '#1e293b',
                              fontWeight: '500'
                            }}>
                              {Array.isArray(clients) ? clients.find(c => c.client_id === Number(formData.clientName))?.clientName || formData.clientName : formData.clientName}
                            </div>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Client Email
                            </span>
                            <div style={{
                              padding: '12px 16px',
                              backgroundColor: 'white',
                              borderRadius: '10px',
                              border: '1px solid #e2e8f0',
                              fontSize: '16px',
                              color: '#1e293b',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              📧 {formData.clientEmail}
                            </div>
                          </div>
                        </>
                      )}
                      {formData.businessType === 'Vendor' && (
                        <>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Vendor Name
                            </span>
                            <div style={{
                              padding: '12px 16px',
                              backgroundColor: 'white',
                              borderRadius: '10px',
                              border: '1px solid #e2e8f0',
                              fontSize: '16px',
                              color: '#1e293b',
                              fontWeight: '500'
                            }}>
                              {Array.isArray(vendors) ? vendors.find(v => v.vendor_id === Number(formData.vendorName))?.vendorName || formData.vendorName : formData.vendorName}
                            </div>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Vendor Email
                            </span>
                            <div style={{
                              padding: '12px 16px',
                              backgroundColor: 'white',
                              borderRadius: '10px',
                              border: '1px solid #e2e8f0',
                              fontSize: '16px',
                              color: '#1e293b',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              📧 {formData.vendorEmail}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Project & Status Section */}
                <div style={{
                  marginBottom: '30px',
                  padding: '25px',
                  backgroundColor: '#f0fdf4',
                  borderRadius: '15px',
                  border: '1px solid #bbf7d0'
                }}>
                  <h3 style={{
                    margin: '0 0 20px 0',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#14532d',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    🚀 Project & Status
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '20px'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Project
                      </span>
                      <div style={{
                        padding: '12px 16px',
                        backgroundColor: 'white',
                        borderRadius: '10px',
                        border: '1px solid #e2e8f0',
                        fontSize: '16px',
                        color: '#1e293b',
                        fontWeight: '500'
                      }}>
                        🎯 {Array.isArray(projects) ? projects.find(p => p.id === Number(formData.project_id))?.name || formData.project_id : formData.project_id}
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Status
                      </span>
                      <div style={{
                        padding: '12px 16px',
                        backgroundColor: 'white',
                        borderRadius: '10px',
                        border: '1px solid #e2e8f0',
                        fontSize: '16px',
                        color: '#1e293b',
                        fontWeight: '500'
                      }}>
                        📊 {Array.isArray(statuses) ? statuses.find(s => s.id === Number(formData.status_id))?.value || formData.status_id : formData.status_id}
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Region
                      </span>
                      <div style={{
                        padding: '12px 16px',
                        backgroundColor: 'white',
                        borderRadius: '10px',
                        border: '1px solid #e2e8f0',
                        fontSize: '16px',
                        color: '#1e293b',
                        fontWeight: '500'
                      }}>
                        🌍 {Array.isArray(regions) ? regions.find(r => r.id === Number(formData.region_id))?.value || formData.region_id : formData.region_id}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Timeline Section */}
                <div style={{
                  marginBottom: '30px',
                  padding: '25px',
                  backgroundColor: '#fefce8',
                  borderRadius: '15px',
                  border: '1px solid #fde047'
                }}>
                  <h3 style={{
                    margin: '0 0 20px 0',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#a16207',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    📅 Timeline
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '20px'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Start Date
                      </span>
                      <div style={{
                        padding: '12px 16px',
                        backgroundColor: 'white',
                        borderRadius: '10px',
                        border: '1px solid #e2e8f0',
                        fontSize: '16px',
                        color: '#1e293b',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        🟢 {formData.startDate}
                      </div>
                    </div>
                    {!isAutoRenewal && (
                      <>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            End Date
                          </span>
                          <div style={{
                            padding: '12px 16px',
                            backgroundColor: 'white',
                            borderRadius: '10px',
                            border: '1px solid #e2e8f0',
                            fontSize: '16px',
                            color: '#1e293b',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            🔴 {formData.endDate}
                          </div>
                        </div>

                        {!isEndDatePast && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Renewal Date
                            </span>
                            <div style={{
                              padding: '12px 16px',
                              backgroundColor: 'white',
                              borderRadius: '10px',
                              border: '1px solid #e2e8f0',
                              fontSize: '16px',
                              color: '#1e293b',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              🔄 {formData.renewalDate}
                            </div>
                          </div>
                        )}
                        {/* Termination Date */}
                        {!isAutoRenewal && formData.termination_date && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Termination Date
                            </span>
                            <div style={{
                              padding: '12px 16px',
                              backgroundColor: 'white',
                              borderRadius: '10px',
                              border: '1px solid #e2e8f0',
                              fontSize: '16px',
                              color: '#1e293b',
                              fontWeight: '500'
                            }}>
                              {formData.termination_date}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                  </div>
                </div>

                {(isAmendment || isAddendum || ["Statement of Work(SOW)", "Change Request(CR)", "Purchase Order(PO)", "Addendum SOW"].includes(getContractTypeLabel())) && formData.parent_contract_id && (
                  <div style={{
                    marginBottom: '30px',
                    padding: '25px',
                    backgroundColor: '#faf5ff',
                    borderRadius: '15px',
                    border: '1px solid #d8b4fe'
                  }}>
                    <h3 style={{
                      margin: '0 0 20px 0',
                      fontSize: '20px',
                      fontWeight: '600',
                      color: '#581c87',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      🔗 Parent Contract
                    </h3>

                    {isAddendum && (
                      <div style={{
                        marginBottom: '15px',
                        color: '#ff6b35',
                        fontWeight: '600',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        📄 Addendum: Yes
                      </div>
                    )}

                    {isAmendment && (
                      <div style={{
                        marginBottom: '15px',
                        color: '#ff6b35',
                        fontWeight: '600',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        🔧 Amendment: Yes
                      </div>
                    )}

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <span style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#64748b',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Parent Contract Title
                      </span>
                      <div style={{
                        padding: '12px 16px',
                        backgroundColor: 'white',
                        borderRadius: '10px',
                        border: '1px solid #e2e8f0',
                        fontSize: '16px',
                        color: '#1e293b',
                        fontWeight: '500'
                      }}>
                        {Array.isArray(parentContracts)
                          ? parentContracts.find(pc => pc.contract_id === Number(formData.parent_contract_id))?.contract_title || formData.parent_contract_id
                          : formData.parent_contract_id}
                      </div>
                    </div>
                  </div>
                )}
                {/* === NEW FIELDS: PO Number, Net Term, Currency, Country, Termination === */}
                {(formData.po_number ||
                  formData.net_term ||
                  formData.bill_rate_currency ||
                  formData.billing_country ||
                  formData.termination_date ||
                  formData.termination_reason) && (
                    <div style={{
                      marginBottom: '30px',
                      padding: '25px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '15px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <h3 style={{
                        margin: '0 0 20px 0',
                        fontSize: '20px',
                        fontWeight: '600',
                        color: '#1e293b',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}>
                        Additional Details
                      </h3>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: '20px'
                      }}>
                        {/* PO Number */}
                        {formData.po_number && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              PO Number
                            </span>
                            <div style={{
                              padding: '12px 16px',
                              backgroundColor: 'white',
                              borderRadius: '10px',
                              border: '1px solid #e2e8f0',
                              fontSize: '16px',
                              color: '#1e293b',
                              fontWeight: '500'
                            }}>
                              {formData.po_number}
                            </div>
                          </div>
                        )}

                        {/* Net Term */}
                        {formData.net_term && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Net Term
                            </span>
                            <div style={{
                              padding: '12px 16px',
                              backgroundColor: 'white',
                              borderRadius: '10px',
                              border: '1px solid #e2e8f0',
                              fontSize: '16px',
                              color: '#1e293b',
                              fontWeight: '500'
                            }}>
                              {formData.net_term} Days
                            </div>
                          </div>
                        )}

                        {/* Bill Rate Currency */}
                        {formData.bill_rate_currency && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Bill Rate Currency
                            </span>
                            <div style={{
                              padding: '12px 16px',
                              backgroundColor: 'white',
                              borderRadius: '10px',
                              border: '1px solid #e2e8f0',
                              fontSize: '16px',
                              color: '#1e293b',
                              fontWeight: '500'
                            }}>
                              {formData.bill_rate_currency}
                            </div>
                          </div>
                        )}

                        {/* Billing Country */}
                        {formData.billing_country && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Billing Country
                            </span>
                            <div style={{
                              padding: '12px 16px',
                              backgroundColor: 'white',
                              borderRadius: '10px',
                              border: '1px solid #e2e8f0',
                              fontSize: '16px',
                              color: '#1e293b',
                              fontWeight: '500'
                            }}>
                              {regions.find(r => String(r.id) === String(formData.billing_country))?.value || 'N/A'}
                            </div>
                          </div>
                        )}
                        {/* Practices */}
                        {formData.practices && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Practices
                            </span>
                            <div style={{
                              padding: '12px 16px',
                              backgroundColor: 'white',
                              borderRadius: '10px',
                              border: '1px solid #e2e8f0',
                              fontSize: '16px',
                              color: '#1e293b',
                              fontWeight: '500'
                            }}>
                              {formData.practices}
                            </div>
                          </div>
                        )}

                        {/* Billing Type */}
                        {formData.billing_type && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Billing Type
                            </span>
                            <div style={{
                              padding: '12px 16px',
                              backgroundColor: 'white',
                              borderRadius: '10px',
                              border: '1px solid #e2e8f0',
                              fontSize: '16px',
                              color: '#1e293b',
                              fontWeight: '500'
                            }}>
                              {formData.billing_type}
                            </div>
                          </div>
                        )}

                        {/* Contract Value */}
                        {formData.contract_value && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Contract Value
                            </span>
                            <div style={{
                              padding: '12px 16px',
                              backgroundColor: 'white',
                              borderRadius: '10px',
                              border: '1px solid #e2e8f0',
                              fontSize: '16px',
                              color: '#1e293b',
                              fontWeight: '500'
                            }}>
                              {formData.currency_type} {formData.contract_value}
                            </div>
                          </div>
                        )}

                        {/* Item Details */}
                        {itemDetails.some(d => d.item || d.quantity || d.hsn_code || d.tax_code) && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', gridColumn: '1 / -1' }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Item Details
                            </span>
                            <div style={{ backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#f8fafc' }}>
                                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>Item</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>Quantity</th>
                                    {itemDetails.some(d => d.hsn_code) && (
                                      <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>HSN Code</th>
                                    )}
                                    {itemDetails.some(d => d.tax_code) && (
                                      <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>Tax Code</th>
                                    )}
                                  </tr>
                                </thead>
                                <tbody>
                                  {itemDetails.filter(d => d.item || d.quantity || d.hsn_code || d.tax_code).map((detail, idx) => (
                                    <tr key={idx}>
                                      <td style={{ padding: '12px', fontSize: '16px', color: '#1e293b', borderBottom: idx < itemDetails.length - 1 ? '1px solid #e2e8f0' : 'none' }}>{detail.item || 'N/A'}</td>
                                      <td style={{ padding: '12px', fontSize: '16px', color: '#1e293b', borderBottom: idx < itemDetails.length - 1 ? '1px solid #e2e8f0' : 'none' }}>{detail.quantity || 'N/A'}</td>
                                      {itemDetails.some(d => d.hsn_code) && (
                                        <td style={{ padding: '12px', fontSize: '16px', color: '#1e293b', borderBottom: idx < itemDetails.length - 1 ? '1px solid #e2e8f0' : 'none' }}>{detail.hsn_code || 'N/A'}</td>
                                      )}
                                      {itemDetails.some(d => d.tax_code) && (
                                        <td style={{ padding: '12px', fontSize: '16px', color: '#1e293b', borderBottom: idx < itemDetails.length - 1 ? '1px solid #e2e8f0' : 'none' }}>{detail.tax_code || 'N/A'}</td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Subsidiary */}
                        {formData.subsidiary && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Subsidiary
                            </span>
                            <div style={{
                              padding: '12px 16px',
                              backgroundColor: 'white',
                              borderRadius: '10px',
                              border: '1px solid #e2e8f0',
                              fontSize: '16px',
                              color: '#1e293b',
                              fontWeight: '500'
                            }}>
                              {formData.subsidiary}
                            </div>
                          </div>
                        )}

                        {/* Billing Frequency */}
                        {formData.billing_frequency && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Billing Frequency
                            </span>
                            <div style={{
                              padding: '12px 16px',
                              backgroundColor: 'white',
                              borderRadius: '10px',
                              border: '1px solid #e2e8f0',
                              fontSize: '16px',
                              color: '#1e293b',
                              fontWeight: '500'
                            }}>
                              {formData.billing_frequency}
                            </div>
                          </div>
                        )}

                        {/* Execution Type */}
                        {formData.execution_type && formData.execution_type.length > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Execution Type
                            </span>
                            <div style={{
                              padding: '12px 16px',
                              backgroundColor: 'white',
                              borderRadius: '10px',
                              border: '1px solid #e2e8f0',
                              fontSize: '16px',
                              color: '#1e293b',
                              fontWeight: '500'
                            }}>
                              {Array.isArray(formData.execution_type) ? formData.execution_type.join(', ') : formData.execution_type}
                            </div>
                          </div>
                        )}

                        {/* SOW Details */}
                        {sowDetails.some(d => d.role_name || d.role_rate || d.sow_amount || d.sow_hours) && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', gridColumn: '1 / -1' }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              SOW Details
                            </span>
                            <div style={{ backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#f8fafc' }}>
                                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>Role Name</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>Role Rate</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>SOW Amount</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>SOW Hours</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sowDetails.filter(d => d.role_name || d.role_rate || d.sow_amount || d.sow_hours).map((detail, idx) => (
                                    <tr key={idx}>
                                      <td style={{ padding: '12px', fontSize: '16px', color: '#1e293b', borderBottom: idx < sowDetails.length - 1 ? '1px solid #e2e8f0' : 'none' }}>{detail.role_name || 'N/A'}</td>
                                      <td style={{ padding: '12px', fontSize: '16px', color: '#1e293b', borderBottom: idx < sowDetails.length - 1 ? '1px solid #e2e8f0' : 'none' }}>{detail.role_rate || 'N/A'}</td>
                                      <td style={{ padding: '12px', fontSize: '16px', color: '#1e293b', borderBottom: idx < sowDetails.length - 1 ? '1px solid #e2e8f0' : 'none' }}>{detail.sow_amount || 'N/A'}</td>
                                      <td style={{ padding: '12px', fontSize: '16px', color: '#1e293b', borderBottom: idx < sowDetails.length - 1 ? '1px solid #e2e8f0' : 'none' }}>{detail.sow_hours || 'N/A'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}



                        {/* Termination Reason */}
                        {formData.termination_reason && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Termination Reason
                            </span>
                            <div style={{
                              padding: '12px 16px',
                              backgroundColor: 'white',
                              borderRadius: '10px',
                              border: '1px solid #e2e8f0',
                              fontSize: '16px',
                              color: '#1e293b',
                              fontWeight: '500',
                              lineHeight: '1.6'
                            }}>
                              {formData.termination_reason}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                {/* Description Section */}
                <div style={{
                  marginBottom: '30px',
                  padding: '25px',
                  backgroundColor: '#f1f5f9',
                  borderRadius: '15px',
                  border: '1px solid #cbd5e1'
                }}>
                  <h3 style={{
                    margin: '0 0 20px 0',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#334155',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    📝 Description
                  </h3>
                  <div style={{
                    padding: '16px 20px',
                    backgroundColor: 'white',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0',
                    fontSize: '16px',
                    color: '#1e293b',
                    lineHeight: '1.6',
                    minHeight: '80px'
                  }}>
                    {formData.description}
                  </div>
                </div>


                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingTop: '25px',
                  borderTop: '2px solid #e2e8f0'
                }}>
                  <button
                    type="button"
                    onClick={() => setIsPreviewModalOpen(false)}
                    style={{
                      backgroundColor: '#6b7280',
                      color: 'white',
                      border: 'none',
                      padding: '14px 28px',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 4px 12px rgba(107, 114, 128, 0.3)'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = '#4b5563';
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 16px rgba(107, 114, 128, 0.4)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = '#6b7280';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(107, 114, 128, 0.3)';
                    }}
                  >
                    ✏️ Edit Contract
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateFromPreview}
                    disabled={isLoading}
                    style={{
                      backgroundColor: isLoading ? '#9ca3af' : '#10b981',
                      color: 'white',
                      border: 'none',
                      padding: '14px 28px',
                      borderRadius: '12px',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s ease',
                      boxShadow: isLoading ? 'none' : '0 4px 12px rgba(16, 185, 129, 0.3)'
                    }}
                    onMouseOver={(e) => {
                      if (!isLoading) {
                        e.target.style.backgroundColor = '#059669';
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isLoading) {
                        e.target.style.backgroundColor = '#10b981';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                      }
                    }}
                  >
                    {isLoading ? '⏳ Creating...' : '🚀 Create Contract'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={5000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
          sx={{
            '& .MuiSnackbarContent-root': {
              backgroundColor: isSuccess ? '#4caf50' : '#f44336',
            },
            zIndex: 1400,
          }}
        />

      </div>
    </div>
  );

};

export default CreateBlankModal;
